import { PauseCircle, PlayCircle } from 'lucide-react';
import { useRef, useState } from 'react';

import { Answers, Question } from '../../redux/Slices/InterviewSlice/types';
import { useGetFileFromS3Mutation } from '../../services/CloudApi';

const QuestionAnalysis = ({
  questionNo,
  question,
  allQuestions,
  isPlayingAudio,
  setIsPlayingAudio,
  shouldShowIdealAnswer
}: {
  questionNo: number;
  question: Answers;
  allQuestions: Question[];
  isPlayingAudio: boolean;
  shouldShowIdealAnswer: boolean;
  setIsPlayingAudio: (isPlaying: boolean) => void;
}) => {
  const [showFullTranscript, setShowFullTranscript] = useState(false);
  const aiQuestionEvaluation = question?.aiQuestionEvaluation;
  const [getFileFromS3] = useGetFileFromS3Mutation();
  const [isPlayingResponseAudio, setIsPlayingResponseAudio] = useState(false);
  const [isPlayingQuestionAudio, setIsPlayingQuestionAudio] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const playQuestion = async (pathUrl: string, playingType: string) => {
    const isQuestionPlaying = playingType === 'question';
    if (isQuestionPlaying && isPlayingQuestionAudio) {
      audioRef.current?.pause();
      setIsPlayingQuestionAudio(false);
      setIsPlayingAudio(false);
      return;
    } else if (!isQuestionPlaying && isPlayingResponseAudio) {
      audioRef.current?.pause();
      setIsPlayingResponseAudio(false);
      setIsPlayingAudio(false);
      return;
    }
    let url = '';
    if (pathUrl) {
      url = await getFileFromS3(pathUrl).unwrap();
    }
    if (audioRef.current && url) {
      setIsPlayingAudio(true);
      isQuestionPlaying
        ? setIsPlayingQuestionAudio(true)
        : setIsPlayingResponseAudio(true);

      // Set up audio element
      audioRef.current.src = url;
      audioRef.current.load();

      // Handle audio end event
      audioRef.current.onended = () => {
        setIsPlayingAudio(false);
        isQuestionPlaying
          ? setIsPlayingQuestionAudio(false)
          : setIsPlayingResponseAudio(false);
      };

      // Handle audio error
      audioRef.current.onerror = () => {
        setIsPlayingAudio(false);
        isQuestionPlaying
          ? setIsPlayingQuestionAudio(false)
          : setIsPlayingResponseAudio(false);
      };

      try {
        await audioRef.current.play();
      } catch (error) {
        console.error('Error playing audio:', error);
        isQuestionPlaying
          ? setIsPlayingQuestionAudio(false)
          : setIsPlayingResponseAudio(false);
      }
    }
  };
  const playOriginalQuestion = (id: any) => {
    if (isPlayingAudio && !isPlayingQuestionAudio) return;
    const QuestionAudiofilePth = allQuestions.find((q: any) => q.id === id)
      ?.audioFile?.fileName;
    QuestionAudiofilePth && playQuestion(QuestionAudiofilePth, 'question');
  };
  const playAnswer = () => {
    if (isPlayingAudio && !isPlayingResponseAudio) return;
    question?.uploadedAnswerFile?.fileName &&
      playQuestion(question?.uploadedAnswerFile?.fileName, 'response');
  };
  const truncatedTranscript =
    aiQuestionEvaluation?.userAnswerTranscript?.length > 300
      ? `${aiQuestionEvaluation.userAnswerTranscript.substring(0, 300)}...`
      : aiQuestionEvaluation.userAnswerTranscript;

  return (
    <div className='mb-8 rounded-2xl bg-gray-50 p-2 md:p-8'>
      <div className='mb-6 flex flex-col items-start justify-between gap-2 md:flex-row md:items-center md:gap-0'>
        <h3 className='flex max-w-[95%] text-lg font-semibold text-gray-800 md:max-w-[75%] md:text-xl'>
          Question: {questionNo} {question?.question}
          <button
            onClick={() => playOriginalQuestion(question?.questionId)}
            className={
              'ml-3 flex items-center gap-2 px-3 py-1 font-medium text-blue-600'
            }
          >
            {!isPlayingQuestionAudio ? (
              <PlayCircle className='h-5 w-5 text-blue-600' />
            ) : (
              <PauseCircle className='h-5 w-5 text-blue-600' />
            )}
          </button>
        </h3>
        <span
          className={`rounded-full px-3 py-1 text-sm font-semibold ${aiQuestionEvaluation?.isRelavent ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}
        >
          {aiQuestionEvaluation?.isRelavent ? 'RELEVANT' : 'NOT RELEVANT'}
        </span>
      </div>
      <audio ref={audioRef} />
      <div className='mb-6 rounded-xl border-l-4 border-indigo-500 bg-white p-2 shadow-sm md:p-6'>
        <h4 className='mb-4 flex font-semibold text-[#555]'>
          Your Response:
          <button
            onClick={() => playAnswer()}
            className={`ml-3 flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium transition-colors ${
              isPlayingResponseAudio ? 'bg-gray-300 text-gray-500' : ''
            }`}
          >
            {!isPlayingResponseAudio ? (
              <PlayCircle className='h-5 w-5 text-blue-600' />
            ) : (
              <PauseCircle className='h-5 w-5 text-blue-600' />
            )}
          </button>
        </h4>
        <p className='leading-relaxed text-gray-600'>
          {showFullTranscript
            ? aiQuestionEvaluation?.userAnswerTranscript
            : truncatedTranscript}
        </p>
        {aiQuestionEvaluation?.userAnswerTranscript?.length > 300 && (
          <button
            onClick={() => setShowFullTranscript(!showFullTranscript)}
            className='mt-3 text-sm font-medium text-indigo-600 transition-colors hover:text-indigo-800'
          >
            {showFullTranscript ? 'Show Less' : 'Show More'}
          </button>
        )}
      </div>
      {shouldShowIdealAnswer && (
        <div className='mb-5 rounded-xl border-l-4 border-blue-400 bg-gradient-to-r from-[#f0f8ff] to-[#e8f0fa] p-2 md:p-6'>
          <div className='mb-4 flex items-center'>
            <h4 className='font-semibold text-[#334155]'>Ideal Answer</h4>
          </div>
          <p className='leading-relaxed text-[#1e293b]'>
            {question?.idealAnswer}
          </p>
        </div>
      )}

      <div className='rounded-xl border-l-4 border-blue-500 bg-gradient-to-r from-[#e3f2fd] to-[#bbdefb] p-2 md:p-6'>
        <div className='mb-4 flex items-center'>
          <h4 className='font-semibold text-[#1976d2]'>
            Improvement Suggestions
          </h4>
        </div>
        <p className='leading-relaxed text-[#1565c0]'>
          {aiQuestionEvaluation?.suggestion}
        </p>
      </div>
    </div>
  );
};

export default QuestionAnalysis;
